import { useRef, useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { fmt } from '../../utils/format';
import { useApi } from '../../hooks/useApi';
import { sanitizeNumericInput, UNSIGNED } from '../../utils/numericInput';
import { Btn, Input, Select, Textarea, Modal, ModalFooter, Table, KpiCard, Spinner, ErrorMsg, InfoNote, EmptyState, useToast } from '../ui';

// ---------------------------------------------------------------------------
// CapitalScheduleTab — anticipated capital calls & distributions for one
// Active / Passive deal detail, and the IRR they imply.
//
// The Active/Passive counterpart to RealEstateBudgetTab. A passive position has
// no buildings and no NOI, so there is no pro forma to run: the schedule IS the
// model. Enter when money goes out and when it comes back; the server expands
// recurrences, resolves percent-of-commitment lines and solves the IRR.
//
// THE SCHEDULE IS A GRID, NOT A LIST OF DIALOGS. Typing a schedule in is the
// common case and it is a spreadsheet-shaped job — a date, and an amount in one
// of two columns — so that is what the Schedule tab is: rows of
// date · capital call · distribution · note, edited in place and saved in one
// request. Which column the amount sits in IS the direction, so there is no
// direction picker to get wrong and a call can never be typed as a distribution.
//
// A LINE THE GRID CANNOT REPRESENT IS SHOWN, NOT HIDDEN. A recurring line or a
// percent-of-commitment line has no single amount to put in a cell (one row
// stands for twenty payments, or for a figure that moves with the commitment),
// so it renders as a locked row carrying what it resolves to, with an Edit that
// opens the full form. Hiding them would make the grid a lie about the total.
//
// NOTHING IS COMPUTED HERE. Every dollar figure and every rate on this screen
// comes from the server's `computed` block (api/src/capitalSchedule.js) — a
// client-side mirror of the resolver would be one more thing to keep in
// lockstep, and this one has no offline surface that needs it.
// ---------------------------------------------------------------------------

const DIRECTIONS = [
  { value: 'Call', label: 'Capital call (money out)' },
  { value: 'Distribution', label: 'Distribution (money back)' },
];
const CATEGORIES = ['Capital Call', 'Return of Capital', 'Preferred Return', 'Profit Distribution', 'Fee', 'Other'];
const METHODS = [
  { value: 'Amount', label: '$ (flat amount)' },
  { value: 'PercentOfCommitment', label: '% of committed capital' },
];
const RECURRENCES = [
  { value: '', label: 'One-off' },
  { value: '1', label: 'Monthly' },
  { value: '3', label: 'Quarterly' },
  { value: '6', label: 'Semi-annually' },
  { value: '12', label: 'Annually' },
];
const RECURRENCE_LABEL = { 1: 'Monthly', 3: 'Quarterly', 6: 'Semi-annual', 12: 'Annual' };

const EMPTY_LINE = {
  direction: 'Call', category: 'Capital Call', description: '',
  scheduledDate: '', method: 'Amount', inputValue: '',
  recurrenceMonths: '', occurrenceCount: '',
};

const mult = (v) => (v == null || isNaN(Number(v)) ? '—' : `${Number(v).toFixed(2)}×`);
const signed = (v) => (v == null ? '—' : `${v < 0 ? '−' : ''}${fmt.currency(Math.abs(v))}`);

// A line the grid can edit in place: one date, one flat amount. Anything else
// (a recurrence, a percent of commitment) needs the full form — see the header.
const isSimpleLine = (l) => (l.method || 'Amount') === 'Amount'
  && !(Number(l.recurrenceMonths) > 0);

// Identity of the server's line set, so the grid remounts on real change and
// not on every render.
const linesSignature = (lines) => lines.map((l) => [
  l.lineID, l.direction, l.scheduledDate, l.method, l.inputValue,
  l.recurrenceMonths, l.occurrenceCount, l.category, l.description,
].join('\u0001')).join('\u0002');

const recurrenceText = (l) => {
  const every = Number(l.recurrenceMonths) || 0;
  if (!every) return 'One-off';
  const label = RECURRENCE_LABEL[every] || `Every ${every} mo`;
  const n = Number(l.occurrenceCount) || 1;
  return n > 1 ? `${label} × ${n}` : label;
};

// ---------------------------------------------------------------------------

export const CapitalScheduleTab = ({ dealId, dealDetailId }) => {
  const { toast } = useToast();
  const base = `deals/${dealId}/details/${dealDetailId}/capital-schedule`;
  const { data, loading, error, reload } = useApi(() => api.get(base), [dealDetailId]);

  const [busy, setBusy] = useState(false);
  const [lineModal, setLineModal] = useState(null);   // { editing?, form } | null
  const [subTab, setSubTab] = useState('Overview');   // Overview | Schedule | Timeline

  if (loading) return <Spinner label="Loading capital schedule…" />;
  if (error) return <ErrorMsg msg={error.message || 'Failed to load capital schedule'} onRetry={reload} />;

  // Pre-migration-164 database: say so rather than showing an empty schedule
  // that silently refuses every write.
  if (data && data.available === false) {
    return <div style={{ marginTop: 8 }}><InfoNote>{data.reason}</InfoNote></div>;
  }

  const lines = (data && data.lines) || [];
  const computed = (data && data.computed) || {};
  const projected = computed.projected || {};
  const actual = computed.actual || null;
  const blended = computed.blended || null;
  const commitment = computed.commitment || {};
  const events = computed.events || [];
  const byYear = computed.byYear || [];

  // Per-line resolved figures, read off the server's expanded events — the
  // client never re-resolves a percent line or re-expands a recurrence.
  const perLine = {};
  for (const e of events) {
    if (!e.lineID) continue;
    const acc = perLine[e.lineID] || { count: 0, per: e.amount, total: 0 };
    acc.count += 1;
    acc.total += e.amount;
    perLine[e.lineID] = acc;
  }

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); reload(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const saveLine = (form, editing) => run(async () => {
    const payload = {
      direction: form.direction,
      category: form.category || null,
      description: form.description || null,
      scheduledDate: form.scheduledDate,
      method: form.method,
      inputValue: form.inputValue,
      recurrenceMonths: form.recurrenceMonths === '' ? null : Number(form.recurrenceMonths),
      occurrenceCount: form.recurrenceMonths === '' ? null : (form.occurrenceCount === '' ? null : Number(form.occurrenceCount)),
    };
    if (editing) await api.put(`${base}/lines/${editing.lineID}`, payload);
    else await api.post(`${base}/lines`, payload);
    setLineModal(null);
  });

  // The grid's whole edit, in one request and one transaction — see
  // saveCapitalScheduleLines. Looping the per-line routes would leave a failure
  // half applied, with the screen still showing the edit that did not land.
  const saveGrid = ({ upsert, remove }) => run(async () => {
    await api.put(`${base}/lines`, { upsert, remove });
  });

  const lineToForm = (l) => ({
    direction: l.direction, category: l.category || '', description: l.description || '',
    scheduledDate: l.scheduledDate || '', method: l.method || 'Amount',
    inputValue: l.inputValue ?? '',
    recurrenceMonths: l.recurrenceMonths == null || l.recurrenceMonths === 0 ? '' : String(l.recurrenceMonths),
    occurrenceCount: l.occurrenceCount ?? '',
  });

  return (
    <div style={{ marginTop: 8 }}>
      {/* ── Mini sub-tabs ────────────────────────────────────────────────── */}
      <div style={{ display: 'inline-flex', gap: 2, padding: 3, background: B.surfaceMuted, border: `1px solid ${B.border}`, borderRadius: 10, marginBottom: 18 }}>
        {['Overview', 'Schedule', 'Timeline'].map((t) => (
          <button key={t} onClick={() => setSubTab(t)} style={{
            border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
            padding: '6px 14px', borderRadius: 8, whiteSpace: 'nowrap',
            background: subTab === t ? B.surface : 'transparent',
            color: subTab === t ? B.navy : B.textMd,
            boxShadow: subTab === t ? B.shadowSm : 'none',
          }}>{t}</button>
        ))}
      </div>

      {/* ══ OVERVIEW ═════════════════════════════════════════════════════ */}
      {subTab === 'Overview' && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14, marginBottom: 18 }}>
          <KpiCard label="Projected IRR" value={projected.irr != null ? fmt.pct(projected.irr) : '—'}
            sub={projected.irr == null ? 'needs a call and a distribution' : 'over the whole schedule'} color={B.green} />
          <KpiCard label="Equity Multiple" value={mult(projected.equityMultiple)} sub="distributions ÷ called" color={B.gold} />
          <KpiCard label="Total Called" value={fmt.currency(projected.called)} sub={`${projected.eventCount || 0} scheduled event${projected.eventCount === 1 ? '' : 's'}`} />
          <KpiCard label="Total Distributed" value={fmt.currency(projected.distributed)} sub={`Net ${signed(projected.net)}`} />
        </div>

        {/* Commitment against what the schedule actually calls */}
        {commitment.committedCapital != null && (
          <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 18, boxShadow: B.shadowSm }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26 }}>
              <Metric k="Committed Capital" v={fmt.currency(commitment.committedCapital)} />
              <Metric k="Scheduled to be called" v={fmt.currency(commitment.called)} />
              <Metric k="Unfunded" v={fmt.currency(commitment.unfunded)} color={commitment.unfunded > 0 ? B.amber : B.textMd} />
              <Metric k="% of commitment" v={commitment.percentCalled != null ? fmt.pct(commitment.percentCalled) : '—'} />
            </div>
            {commitment.unfunded > 0 && (
              <div style={{ fontSize: 11, color: B.textLt, marginTop: 10 }}>
                The schedule calls less than the commitment — either capital is still to be scheduled, or the position is not fully drawn.
              </div>
            )}
          </div>
        )}

        {/* Projected vs what Sage says actually happened */}
        {actual ? (
          <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, marginBottom: 18, boxShadow: B.shadowSm, overflow: 'hidden' }}>
            <div style={{ padding: '10px 18px', borderBottom: `1px solid ${B.divider}`, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: B.textMd, fontWeight: 700 }}>
              Projected vs actual — actuals from Sage, as of {fmt.date(computed.asOf)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <Cell k="Projected IRR" v={projected.irr != null ? fmt.pct(projected.irr) : '—'} sub="the plan, start to exit" />
              <Cell k="Actual IRR to date" v={actual.irr != null ? fmt.pct(actual.irr) : '—'} sub={`${fmt.currency(actual.called)} in · ${fmt.currency(actual.distributed)} back`} />
              <Cell k="Blended IRR" v={blended && blended.irr != null ? fmt.pct(blended.irr) : '—'} sub="actuals to date, projection after" />
            </div>
            <div style={{ padding: '10px 18px', fontSize: 11, color: B.textLt, borderTop: `1px solid ${B.divider}` }}>
              An IRR on capital that has gone out but not yet come back is deeply negative by construction — the blend is the number to read
              while the position is still running.
            </div>
          </div>
        ) : (
          <InfoNote>
            {data && data.hasSageLink
              ? 'No Sage activity on this detail’s location yet, so the schedule is a pure projection.'
              : 'This deal detail has no Sage location tagged, so there is nothing to compare the projection against. Tag one on the detail to see actual and blended returns.'}
          </InfoNote>
        )}

        {byYear.length > 0 && (
          <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, marginTop: 18, boxShadow: B.shadowSm }}>
            <YearChart rows={byYear} />
          </div>
        )}
      </>}

      {/* ══ SCHEDULE (the editable grid) ═════════════════════════════════ */}
      {subTab === 'Schedule' && (
        <ScheduleGrid
          // Remount when the server's lines actually change (i.e. after a save
          // or a reload), so the draft rows are re-seeded from the truth rather
          // than reconciled against it by hand.
          key={linesSignature(lines)}
          lines={lines}
          perLine={perLine}
          projected={projected}
          busy={busy}
          onSaveGrid={saveGrid}
          onEditAdvanced={(l) => setLineModal({ editing: l, form: lineToForm(l) })}
          onAddAdvanced={() => setLineModal({ form: { ...EMPTY_LINE, recurrenceMonths: '3', occurrenceCount: 4 } })}
        />
      )}

      {/* ══ TIMELINE (every expanded event) ══════════════════════════════ */}
      {subTab === 'Timeline' && <>
        <SectionBar title={blended ? 'Every cash flow — actuals to date, projection after' : 'Every projected cash flow'} />
        {(() => {
          const rows = blended ? blended.events : events;
          if (!rows.length) return <EmptyState title="Nothing scheduled" message="Add a line on the Schedule tab." />;
          let running = 0;
          const withRunning = rows.map((e) => { running += e.signedAmount; return { ...e, running }; });
          return (
            <Table
              columns={[
                { key: 'date', label: 'Date', render: (v) => fmt.date(v) },
                { key: 'source', label: 'Source', render: (v) => (
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                    padding: '2px 7px', borderRadius: 999,
                    background: v === 'actual' ? B.navy : B.surfaceMuted,
                    color: v === 'actual' ? B.white : B.textMd,
                    border: `1px solid ${v === 'actual' ? B.navy : B.border}`,
                  }}>{v === 'actual' ? 'Actual' : 'Projected'}</span>
                ) },
                { key: 'category', label: 'Category', render: (v) => v || '—' },
                { key: 'description', label: 'Description', render: (v, r) => v || (r.occurrences > 1 ? `Payment ${r.occurrence} of ${r.occurrences}` : '—') },
                { key: 'signedAmount', label: 'Amount', align: 'right', render: (v) => (
                  <span style={{ color: v < 0 ? B.red : B.green, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{signed(v)}</span>
                ) },
                { key: 'running', label: 'Cumulative', align: 'right', render: (v) => (
                  <span style={{ color: v < 0 ? B.textMd : B.green, fontVariantNumeric: 'tabular-nums' }}>{signed(v)}</span>
                ) },
              ]}
              data={withRunning}
            />
          );
        })()}
      </>}

      {lineModal && (
        <LineModal
          key={(lineModal.editing && lineModal.editing.lineID) || 'new'}
          state={lineModal}
          busy={busy}
          committedCapital={commitment.committedCapital}
          onClose={() => setLineModal(null)}
          onSave={(form) => saveLine(form, lineModal.editing)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------

const Metric = ({ k, v, color }) => (
  <div>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: B.textLt, fontWeight: 600 }}>{k}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 1, color: color || B.text, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
  </div>
);

const Cell = ({ k, v, sub }) => (
  <div style={{ padding: '12px 18px', borderRight: `1px solid ${B.divider}` }}>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textLt, fontWeight: 600 }}>{k}</div>
    <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
    {sub && <div style={{ fontSize: 11, color: B.textLt, marginTop: 2 }}>{sub}</div>}
  </div>
);

const SectionBar = ({ title, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 12px' }}>
    <span style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.6, color: B.textMd, fontWeight: 700 }}>{title}</span>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// ScheduleGrid — the schedule as a spreadsheet: one row per line, a date, and
// the amount in ONE of two columns. Which column it is in decides the direction,
// so there is no direction control to disagree with the number beside it.
//
// Draft rows live here and nowhere else; the server is told about them once, on
// Save. Nothing is resolved or expanded on this side (see the header) — a saved
// row shows the figure the server resolved it to, and the totals below are the
// server's, which is why they wait for a save to catch up.
// ---------------------------------------------------------------------------

const blankRow = (n) => ({ key: `new-${n}`, lineID: null, date: '', call: '', dist: '', note: '', origin: null });

const toRow = (l) => ({
  key: l.lineID,
  lineID: l.lineID,
  date: l.scheduledDate || '',
  call: l.direction === 'Call' ? String(l.inputValue ?? '') : '',
  dist: l.direction === 'Distribution' ? String(l.inputValue ?? '') : '',
  note: l.description || '',
  origin: l,
});

const amountOf = (r) => (r.call !== '' ? r.call : r.dist);
const directionOf = (r) => (r.call !== '' ? 'Call' : 'Distribution');
const rowHasContent = (r) => r.date !== '' || r.call !== '' || r.dist !== '' || r.note.trim() !== '';

// A row differs from what the server holds. Amounts compare NUMERICALLY —
// DECIMAL(18,4) comes back as "1000.0000" and re-saving every row on every
// visit because the string differs would be a write per row for no change.
const rowChanged = (r) => {
  if (!r.origin) return rowHasContent(r);
  return r.date !== (r.origin.scheduledDate || '')
    || r.note !== (r.origin.description || '')
    || directionOf(r) !== r.origin.direction
    || Number(amountOf(r)) !== Number(r.origin.inputValue);
};

// Name a row by what is IN it. "Row 2" makes the reader count rows to find the
// one being complained about, and counts wrong the moment a row is added above.
const rowLabel = (r, i) => {
  const note = r.note.trim();
  const amt = amountOf(r);
  const name = note || (amt !== '' && !isNaN(Number(amt)) ? fmt.currency(Number(amt)) : '');
  return name ? `Row ${i + 1} (${name})` : `Row ${i + 1}`;
};

const CELL = { border: 'none', outline: 'none', background: 'transparent', font: 'inherit', width: '100%', padding: '7px 8px', color: B.text };
const NUM_CELL = { ...CELL, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const HEAD = { fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: B.textLt, fontWeight: 700, padding: '0 8px 6px', textAlign: 'left' };
const GRID_COLS = '150px 1fr 1fr 1.6fr 92px';

const ScheduleGrid = ({ lines, perLine, projected, busy, onSaveGrid, onEditAdvanced, onAddAdvanced }) => {
  const [rows, setRows] = useState(() => lines.map(toRow));
  const [removed, setRemoved] = useState([]);
  const [err, setErr] = useState(null);
  // A ref, not state: two quick clicks on "Add a date" would both read the same
  // stale counter and mint two rows with the same key.
  const nextKey = useRef(1);

  const setCell = (key, field, value) => {
    // A REFUSAL IS ABOUT A SAVE ATTEMPT, SO EDITING ANYTHING RETIRES IT. Nothing
    // used to clear it but a removed row or a successful save, so filling in the
    // very cell it named left the message on screen contradicting the grid —
    // "Row 2 needs a date" above a row plainly showing a date. React bails out
    // when it is already null, so this costs nothing per keystroke.
    setErr(null);
    setRows((rs) => rs.map((r) => {
    if (r.key !== key) return r;
    // The two amount columns are mutually exclusive: typing into one clears the
    // other, so a row can never carry an amount in both and leave the direction
    // ambiguous.
    if (field === 'call') return { ...r, call: value, dist: value === '' ? r.dist : '' };
    if (field === 'dist') return { ...r, dist: value, call: value === '' ? r.call : '' };
    return { ...r, [field]: value };
    }));
  };

  const addRow = () => {
    setErr(null);
    setRows((rs) => [...rs, blankRow(nextKey.current++)]);
  };

  const dropRow = (r) => {
    if (r.lineID && !window.confirm('Remove this line from the schedule?')) return;
    if (r.lineID) setRemoved((x) => [...x, r.lineID]);
    setRows((rs) => rs.filter((x) => x.key !== r.key));
    setErr(null);
  };

  const dirty = removed.length > 0 || rows.some(rowChanged);

  const save = () => {
    const upsert = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.origin && !rowHasContent(r)) continue;      // an untouched blank row is not an edit
      if (!isSimpleLine(r.origin || {})) continue;        // a locked row is only ever removed here
      if (!rowChanged(r)) continue;

      // A half-filled row is refused by name rather than dropped: silently
      // discarding what someone typed is the worst of the options.
      const amount = amountOf(r);
      if (!r.date) {
        return setErr({ key: r.key, field: 'date', message: `${rowLabel(r, i)} needs a date.` });
      }
      if (amount === '' || isNaN(Number(amount)) || Number(amount) <= 0) {
        return setErr({
          key: r.key,
          field: 'amount',
          message: `${rowLabel(r, i)} needs an amount in the capital call or the distribution column.`,
        });
      }

      const direction = directionOf(r);
      upsert.push({
        lineID: r.lineID || undefined,
        direction,
        // Category is descriptive only and carries no math, so the grid does not
        // ask for one — it keeps whatever the line had, and seeds a new row with
        // the same default the full form uses.
        category: (r.origin && r.origin.direction === direction && r.origin.category)
          || (direction === 'Call' ? 'Capital Call' : 'Profit Distribution'),
        description: r.note.trim() || null,
        scheduledDate: r.date,
        method: 'Amount',
        inputValue: Number(amount),
        recurrenceMonths: null,
        occurrenceCount: null,
        // No stored rank: the grid IS chronological, and a second source of
        // order would be one more thing to keep in step with the dates.
        sortOrder: null,
      });
    }
    setErr(null);
    onSaveGrid({ upsert, remove: removed });
  };

  return (
    <>
      <SectionBar title="Planned cash flows">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {dirty && <span style={{ fontSize: 11, color: B.amber, fontWeight: 600 }}>Unsaved changes</span>}
          <Btn size="sm" variant="ghost" disabled={busy} onClick={onAddAdvanced}>+ Recurring / % line</Btn>
          <Btn size="sm" disabled={busy || !dirty} loading={busy} onClick={save}>Save schedule</Btn>
        </div>
      </SectionBar>

      <div style={{ fontSize: 11, color: B.textLt, marginBottom: 10 }}>
        One row per dated cash flow. Put the amount in <strong>Capital call</strong> when money goes out and in{' '}
        <strong>Distribution</strong> when it comes back — which column it is in is what makes it one or the other.
      </div>

      {err && <div style={{ marginBottom: 10 }}><ErrorMsg msg={err.message} /></div>}

      <div style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 10, boxShadow: B.shadowSm, overflow: 'hidden' }}>
        <div style={{ padding: '12px 10px 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'end' }}>
            <div style={HEAD}>Date</div>
            <div style={{ ...HEAD, textAlign: 'right', color: B.red }}>Capital call</div>
            <div style={{ ...HEAD, textAlign: 'right', color: B.green }}>Distribution</div>
            <div style={HEAD}>Note</div>
            <div style={{ ...HEAD, textAlign: 'right' }} />
          </div>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: '26px 18px', textAlign: 'center', color: B.textMd, fontSize: 13 }}>
            Nothing scheduled yet — add the first date below.
          </div>
        ) : rows.map((r, i) => {
          const simple = isSimpleLine(r.origin || {});
          const resolved = r.origin && perLine[r.origin.lineID];
          // The refusal points AT the row, so nobody has to match a sentence to
          // a line by counting. `bad` fades as soon as the row is touched.
          const bad = err && err.key === r.key ? err.field : null;
          // The same B.red the UI kit's fieldStyle(error) uses for an invalid
          // field — a borderless grid cell wears it as an inset ring instead of
          // a border, so nothing shifts by a pixel when it appears.
          const flag = (field) => (bad === field
            ? { boxShadow: `inset 0 0 0 1.5px ${B.red}`, borderRadius: 6 }
            : null);
          return (
            <div key={r.key} style={{
              display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'center',
              borderTop: `1px solid ${B.divider}`, padding: '0 10px',
              background: simple ? 'transparent' : B.surfaceMuted,
            }}>
              {simple ? <>
                <input type="date" aria-label={`Date, row ${i + 1}`} value={r.date} disabled={busy}
                  aria-invalid={bad === 'date' ? 'true' : undefined}
                  onChange={(e) => setCell(r.key, 'date', e.target.value)}
                  style={{ ...CELL, ...flag('date') }} />
                {/* Text, not type="number": a number input rewrites what it
                    cannot parse, and which characters those are is a property
                    of the engine — Chromium strips a trailing "1250000." to
                    "1250000" and empties a lone "-". A money cell validated on
                    save should not hold different text on an iPad. */}
                <input type="text" inputMode="decimal" placeholder="—"
                  aria-label={`Capital call amount, row ${i + 1}`} value={r.call} disabled={busy}
                  aria-invalid={bad === 'amount' ? 'true' : undefined}
                  onChange={(e) => setCell(r.key, 'call', sanitizeNumericInput(e.target.value, UNSIGNED))}
                  style={{ ...NUM_CELL, ...flag('amount'), color: r.call === '' ? B.textLt : B.red, fontWeight: r.call === '' ? 400 : 600 }} />
                <input type="text" inputMode="decimal" placeholder="—"
                  aria-label={`Distribution amount, row ${i + 1}`} value={r.dist} disabled={busy}
                  aria-invalid={bad === 'amount' ? 'true' : undefined}
                  onChange={(e) => setCell(r.key, 'dist', sanitizeNumericInput(e.target.value, UNSIGNED))}
                  style={{ ...NUM_CELL, ...flag('amount'), color: r.dist === '' ? B.textLt : B.green, fontWeight: r.dist === '' ? 400 : 600 }} />
                <input type="text" placeholder="what this is" aria-label={`Note, row ${i + 1}`} value={r.note} disabled={busy}
                  onChange={(e) => setCell(r.key, 'note', e.target.value)} style={CELL} />
              </> : <>
                {/* A recurring or percent line: no single cell can hold it, so it
                    reads what it resolves to and is edited in the full form. */}
                <div style={{ padding: '7px 8px', fontSize: 13 }}>{fmt.date(r.origin.scheduledDate)}</div>
                <div style={{ ...NUM_CELL, color: r.origin.direction === 'Call' ? B.red : B.textLt, fontWeight: r.origin.direction === 'Call' ? 600 : 400 }}>
                  {r.origin.direction === 'Call' ? (resolved ? fmt.currency(resolved.total) : '—') : '—'}
                </div>
                <div style={{ ...NUM_CELL, color: r.origin.direction === 'Distribution' ? B.green : B.textLt, fontWeight: r.origin.direction === 'Distribution' ? 600 : 400 }}>
                  {r.origin.direction === 'Distribution' ? (resolved ? fmt.currency(resolved.total) : '—') : '—'}
                </div>
                <div style={{ padding: '7px 8px', fontSize: 12, color: B.textMd, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{r.origin.description || '—'}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 7px', borderRadius: 999, background: B.surface, border: `1px solid ${B.border}`, color: B.textMd }}>
                    {recurrenceText(r.origin)}{r.origin.method === 'PercentOfCommitment' ? ' · % of commitment' : ''}
                  </span>
                </div>
              </>}

              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                {!simple && (
                  <Btn size="sm" variant="ghost" style={{ padding: '3px 8px', fontSize: 11 }} disabled={busy}
                    onClick={() => onEditAdvanced(r.origin)}>Edit</Btn>
                )}
                <Btn size="sm" variant="ghost" style={{ padding: '3px 8px', fontSize: 11, color: B.red }} disabled={busy}
                  onClick={() => dropRow(r)} aria-label={`Remove row ${i + 1}`}>✕</Btn>
              </div>
            </div>
          );
        })}

        <div style={{ borderTop: `1px solid ${B.border}`, padding: '8px 10px' }}>
          <Btn size="sm" variant="ghost" disabled={busy} onClick={addRow}>+ Add a date</Btn>
        </div>

        {/* The totals are the SERVER's (see the header) — they catch up on save. */}
        <div style={{ display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'center', borderTop: `2px solid ${B.border}`, background: B.surfaceMuted, padding: '10px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, padding: '0 8px' }}>
            {dirty ? 'Saved total' : 'Total'}
          </div>
          <div style={{ ...NUM_CELL, color: B.red, fontWeight: 700 }}>{fmt.currency(projected.called)}</div>
          <div style={{ ...NUM_CELL, color: B.green, fontWeight: 700 }}>{fmt.currency(projected.distributed)}</div>
          {/* Net and the rate it implies, spanning the note + actions columns.
              The IRR is the SERVER's projected figure over the whole planned
              schedule — the same number the Overview tab leads with, so the two
              can never disagree — which is why it waits for a save like the
              totals beside it. */}
          <div style={{
            gridColumn: 'span 2', padding: '0 8px', fontSize: 12, color: B.textMd,
            display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap',
          }}>
            <span>
              Net{' '}
              <strong style={{ color: projected.net < 0 ? B.red : B.green, fontVariantNumeric: 'tabular-nums' }}>
                {signed(projected.net)}
              </strong>
            </span>
            <span title="Internal rate of return solved from every dated flow in this schedule, calls negative and distributions positive.">
              Projected IRR{' '}
              <strong style={{ color: B.navy, fontVariantNumeric: 'tabular-nums' }}>
                {projected.irr != null ? fmt.pct(projected.irr) : '—'}
              </strong>
            </span>
            {/* An IRR needs money going both ways; saying so beats a bare dash. */}
            {projected.irr == null && (
              <span style={{ color: B.textLt }}>needs a call and a distribution</span>
            )}
            {dirty && <span style={{ color: B.amber }}>save to include your edits</span>}
          </div>
        </div>
      </div>
    </>
  );
};

// Net cash flow by calendar year, with the cumulative line underneath. Bars are
// signed off a shared zero so a year of calls reads as a hole, not a bar.
const YearChart = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.net || 0)));
  return (
    <div style={{ padding: '16px 18px 18px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: B.textMd, fontWeight: 700, marginBottom: 14 }}>
        Net cash flow by year (calls out → distributions back)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${rows.length}, 1fr)`, gap: 10, alignItems: 'center', height: 150 }}>
        {rows.map((r) => {
          const neg = (r.net || 0) < 0;
          const h = Math.round((Math.abs(r.net || 0) / max) * 60);
          return (
            <div key={r.year} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
              <div style={{ height: 62, display: 'flex', alignItems: 'flex-end', width: '100%', justifyContent: 'center' }}>
                {!neg && <div title={fmt.currency(r.net)} style={{ width: '70%', height: `${h}px`, background: B.green, borderRadius: '4px 4px 0 0' }} />}
              </div>
              <div style={{ height: 1, width: '100%', background: B.border }} />
              <div style={{ height: 62, display: 'flex', alignItems: 'flex-start', width: '100%', justifyContent: 'center' }}>
                {neg && <div title={fmt.currency(r.net)} style={{ width: '70%', height: `${h}px`, background: B.red, borderRadius: '0 0 4px 4px' }} />}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${rows.length}, 1fr)`, gap: 10, marginTop: 8 }}>
        {rows.map((r) => (
          <div key={r.year} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: B.text }}>{r.year}</div>
            <div style={{ fontSize: 10, color: B.textLt, fontVariantNumeric: 'tabular-nums' }}>{signed(r.cumulativeNet)}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 10, color: B.textLt, textAlign: 'center', marginTop: 6 }}>cumulative below each year</div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const LineModal = ({ state, busy, committedCapital, onClose, onSave }) => {
  const [form, setForm] = useState(state.form);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const recurring = form.recurrenceMonths !== '';
  const isPercent = form.method === 'PercentOfCommitment';

  // What the entered percent resolves to right now — the same arithmetic the
  // server does, shown only as a hint so the user is not typing blind. The
  // stored value is still the percent; nothing here is persisted.
  const preview = isPercent && committedCapital && form.inputValue !== '' && !isNaN(Number(form.inputValue))
    ? fmt.currency(Number(committedCapital) * Number(form.inputValue) / 100)
    : null;

  const valid = form.direction && form.scheduledDate && form.inputValue !== '' && !isNaN(Number(form.inputValue));

  return (
    <Modal title={state.editing ? 'Edit cash flow' : 'Add cash flow'} onClose={onClose} width={560}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Direction" required value={form.direction} onChange={(v) => set('direction', v)} options={DIRECTIONS} />
          <Select label="Category" value={form.category} onChange={(v) => set('category', v)}
            options={[{ value: '', label: '— None —' }, ...CATEGORIES.map((c) => ({ value: c, label: c }))]} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={recurring ? 'First date' : 'Date'} required type="date" value={form.scheduledDate} onChange={(v) => set('scheduledDate', v)} />
          <Select label="Basis" value={form.method} onChange={(v) => set('method', v)} options={METHODS} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label={isPercent ? '% of committed capital' : 'Amount ($)'}
            required type="number" value={form.inputValue} onChange={(v) => set('inputValue', v)}
          />
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 8, fontSize: 12, color: B.textMd }}>
            {isPercent
              ? (preview ? `= ${preview} each` : 'Set committed capital on the detail to resolve this')
              : (recurring ? 'per occurrence' : '')}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Repeats" value={form.recurrenceMonths} onChange={(v) => setForm((f) => ({
            ...f,
            recurrenceMonths: v,
            // A recurrence with no count expands to a single payment, which is
            // never what picking "Quarterly" meant. Seed one; clear it on the
            // way back to a one-off so a stale count can't linger.
            occurrenceCount: v === '' ? '' : (f.occurrenceCount === '' ? 4 : f.occurrenceCount),
          }))} options={RECURRENCES} />
          {recurring && (
            <Input label="How many payments" type="number" value={form.occurrenceCount} onChange={(v) => set('occurrenceCount', v)} />
          )}
        </div>

        <Textarea label="Description" rows={2} value={form.description} onChange={(v) => set('description', v)}
          placeholder="e.g. Second capital call — construction draw" />

        {isPercent && (
          <div style={{ fontSize: 11, color: B.textLt }}>
            A percent line is resolved against the detail’s committed capital every time the schedule is read, so changing the
            commitment updates this line instead of leaving a stale figure behind.
          </div>
        )}
      </div>

      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={() => onSave(form)} disabled={busy || !valid} loading={busy}>
          {state.editing ? 'Save' : 'Add'}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};
